export const HIPAA_TRAINING_STATUSES = [
  'Overview',
  'Why HIPAA Matters',
  'What To Expect',
  'Video',
  'Question 1',
  'Question 2',
  'Question 3',
  'Question 4',
  'Question 5',
  'Attestation',
  'Completed',
] as const;

export type HipaaTrainingStatus = (typeof HIPAA_TRAINING_STATUSES)[number];
export type HipaaAnswerOption = 'A' | 'B' | 'C' | 'D';

export const HIPAA_TRAINING_ASSET_IDS = {
  overviewImage: 'overview-image',
  thankYouImage: 'thank-you-image',
  trainingVideo: 'training-video',
} as const;

export const HIPAA_TRAINING_COPY = {
  overview: {
    title: 'What is HIPAA?',
    body: [
      'HIPAA training is essential for individuals and organizations that handle protected health information (PHI). The Health Insurance Portability and Accountability Act (HIPAA) was enacted to safeguard patient privacy and ensure healthcare providers, insurers, and business associates comply with strict privacy and security rules.',
      'This training explains the core concepts of HIPAA, including what PHI is, why it matters, and what is expected of EVO Care Group team members when working with sensitive information.',
    ],
  },
  whyHipaaMatters: {
    title: 'Why HIPAA Training Matters for EVO Care Group',
    body: [
      'At EVO Care Group, we are committed to delivering high-quality, compassionate care while upholding the highest standards of privacy and compliance.',
      'Because our teams work with sensitive health information in homes, offices, and digital systems, everyone must understand how to protect patient information and respond appropriately in day-to-day situations.',
    ],
  },
  whatToExpect: {
    title: 'What To Expect in This Training',
    bullets: [
      'Watch the full HIPAA training video.',
      'Answer five scenario-based questions.',
      'Complete a final acknowledgement to finish the training.',
    ],
  },
  knowledgeCheck: {
    title: 'Test Your Knowledge',
    body: 'Now that you have watched the video, answer the following questions to confirm your understanding of HIPAA expectations at EVO Care Group.',
    note:
      'You must answer at least 4 of the 5 questions correctly to continue. If you score below 4, the training will reset and you will need to watch the video again before retrying.',
  },
  attestation: {
    title: 'Your Commitment to Privacy',
    intro:
      'Before finishing, confirm that you understand and agree to the following statements:',
    bullets: [
      'I understand the importance of protecting patient privacy and confidentiality.',
      'I understand that PHI must only be accessed, discussed, and shared when authorized and appropriate.',
      'I understand that failure to follow HIPAA expectations may result in corrective action.',
      'I acknowledge that I am responsible for safeguarding protected health information in my role.',
    ],
    checkboxLabel:
      'I acknowledge that I completed this HIPAA training and understand my responsibilities under HIPAA.',
  },
  completed: {
    title: 'Training Complete',
    body: 'Thank you for completing the HIPAA training.',
  },
} as const;

export const HIPAA_TRAINING_QUESTIONS = [
  {
    status: 'Question 1' as const,
    fieldName: 'question1Answer' as const,
    prompt:
      "You work in the billing department, and a caller claiming to be a client's son asks for details about their medical bills. What should you do?",
    options: [
      { value: 'A' as const, label: 'Provide the information if the caller sounds trustworthy.' },
      {
        value: 'B' as const,
        label:
          'Ask the caller to verify their identity and check if they are an authorized contact.',
      },
      {
        value: 'C' as const,
        label: 'Give only general information, such as the total bill amount, but avoid specifics.',
      },
      {
        value: 'D' as const,
        label: 'Transfer the call to another department without verifying authorization.',
      },
    ],
  },
  {
    status: 'Question 2' as const,
    fieldName: 'question2Answer' as const,
    prompt:
      "A nurse emails you a request for patient records, but you notice the request was sent from their personal Gmail account instead of the agency's secure email. What should you do?",
    options: [
      { value: 'A' as const, label: 'Reply with the requested patient records to avoid delays.' },
      { value: 'B' as const, label: 'Forward the email to a supervisor for review.' },
      {
        value: 'C' as const,
        label:
          'Inform the nurse that PHI should only be sent through secure, agency-approved email channels.',
      },
      {
        value: 'D' as const,
        label: 'Copy and paste the information into a Word document and attach it to your reply.',
      },
    ],
  },
  {
    status: 'Question 3' as const,
    fieldName: 'question3Answer' as const,
    prompt:
      "Consider yourself an HR staff member reviewing an employee's medical leave request. While looking at their file, you notice they were recently hospitalized for a condition unrelated to their leave request. What should you do?",
    options: [
      { value: 'A' as const, label: 'Keep the information in mind in case it is relevant later.' },
      {
        value: 'B' as const,
        label: 'Make a note in their file just in case management needs to know.',
      },
      {
        value: 'C' as const,
        label: 'Close the file and only document information relevant to their leave request.',
      },
      {
        value: 'D' as const,
        label: 'Share it with another HR colleague to confirm if it should be reported to management.',
      },
    ],
  },
  {
    status: 'Question 4' as const,
    fieldName: 'question4Answer' as const,
    prompt:
      "You are on a lunch break in the office and overhear two coworkers discussing a patient's billing issues loudly where others can hear. What should you do?",
    options: [
      { value: 'A' as const, label: 'Ignore it because it is a conversation between coworkers.' },
      {
        value: 'B' as const,
        label: 'Politely remind them that PHI should only be discussed in a private setting.',
      },
      { value: 'C' as const, label: 'Join the conversation since you may have useful input.' },
      { value: 'D' as const, label: 'Report them immediately to HR.' },
    ],
  },
  {
    status: 'Question 5' as const,
    fieldName: 'question5Answer' as const,
    prompt:
      'You are printing patient records for a case review. After printing, you get distracted by a meeting and leave the documents on the printer. What is the correct action?',
    options: [
      {
        value: 'A' as const,
        label: 'Retrieve the documents immediately and place them in a secure location.',
      },
      {
        value: 'B' as const,
        label: 'Leave them there because only authorized staff members use the printer.',
      },
      { value: 'C' as const, label: 'Ask a coworker to pick them up and place them on your desk.' },
      {
        value: 'D' as const,
        label: 'Print another copy later and discard the first set in a regular trash bin.',
      },
    ],
  },
] as const;
